import { Link } from 'react-router-dom';

import { useAuth } from '../../auth/useAuth.ts';
import { InstallSection } from '../install/InstallSection.tsx';
import { NotificationSettings } from '../notifications/NotificationSettings.tsx';
import { ProfileSettings } from './ProfileSettings.tsx';

/**
 * Your account, on its own screen.
 *
 * There is not much here yet — a name you can change and the address you signed up with.
 * It exists as a place: everything personal has somewhere to land now, rather than being
 * wedged into the group settings drawer where it never belonged. The name in particular
 * is not a group setting, and having it there implied it was one.
 */
export function ProfilePage() {
  const { user } = useAuth();

  return (
    <main className="app-shell">
      <header className="page-header">
        <Link className="icon-button" to="/" aria-label="Back to your list">
          <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true" focusable="false">
            <path
              d="M15 5l-7 7 7 7"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </Link>

        <h1 className="page-title">Profile</h1>
      </header>

      <div className="settings">
        <ProfileSettings />

        <section className="settings-section">
          <h3 className="settings-section__title">Account</h3>

          <dl className="detail-list">
            <div className="detail-list__row">
              <dt>Email</dt>
              <dd>{user?.email}</dd>
            </div>

            <div className="detail-list__row">
              <dt>Confirmed</dt>
              <dd>{user?.emailConfirmed ? 'Yes' : 'Not yet'}</dd>
            </div>

            <div className="detail-list__row">
              <dt>Joined</dt>
              <dd>{formatJoined(user?.createdAt)}</dd>
            </div>
          </dl>
        </section>

        {/* Last, and after the account facts rather than among them: this is about the
            phone in your hand, not about who you are signed in as. It draws nothing at all
            when the app is already an icon, which is most of the time. */}
        <InstallSection />

        {/* Below it, because on iPhone notifications only work once the app *is* an icon —
            so when this section says "add it to your home screen first", the section that
            does that is the one directly above rather than somewhere further up the page.
            Same argument for it living on Profile at all: permission belongs to the
            browser, so this describes the device rather than the account. */}
        <NotificationSettings />
      </div>
    </main>
  );
}

/** The device's locale rather than a fixed format: this is read, never parsed. */
function formatJoined(createdAt: string | undefined): string {
  if (!createdAt) {
    return '—';
  }

  const date = new Date(createdAt);

  if (Number.isNaN(date.getTime())) {
    return '—';
  }

  return date.toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' });
}
